import * as Updates from 'expo-updates';
import { useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';

export const useUpdates = () => {
  const appState = useRef(AppState.currentState);
  const lastCheckTime = useRef(0);
  const CHECK_INTERVAL = 1000 * 60 * 30; // 30 minutes
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

  async function onFetchUpdateAsync() {
    if (__DEV__) {
      return;
    }

    // Avoid checking too frequently (e.g., if the user switches apps often)
    const now = Date.now();
    if (now - lastCheckTime.current < 1000 * 60 * 5) { // 5 minutes minimum between checks
      return;
    }
    lastCheckTime.current = now;

    try {
      const update = await Updates.checkForUpdateAsync();

      if (update.isAvailable) {
        await Updates.fetchUpdateAsync();
        setIsUpdateAvailable(true);
      }
    } catch (error) {
      // You can also add an alert or log to an external error reporting service here
      console.error(`Error fetching latest Expo update: ${error}`);
    }
  }

  const performUpdate = async () => {
    try {
      await Updates.reloadAsync();
    } catch (error) {
      console.error(`Error reloading app: ${error}`);
    }
  };

  useEffect(() => {
    if (__DEV__) {
      return;
    }

    // Check on mount
    onFetchUpdateAsync();

    // Check on foreground
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (
        (appState.current === 'background' || appState.current === 'inactive') &&
        nextAppState === 'active'
      ) {
        onFetchUpdateAsync();
      }

      appState.current = nextAppState;
    });

    // Also check periodically if the app stays open for a long time
    const interval = setInterval(() => {
      if (AppState.currentState === 'active') {
        onFetchUpdateAsync();
      }
    }, CHECK_INTERVAL);

    return () => {
      subscription.remove();
      clearInterval(interval);
    };
  }, []);

  return {
    isUpdateAvailable,
    performUpdate,
  };
};
